import LabelInput from 'components/common/forms/LabelInput';
import SingleBtn from 'components/common/elements/SingleBtn';
import { useEffect, useState } from 'react';
import styles from 'styles/pages/common/ResetPassword.module.scss';
import LoginLayout from 'components/common/layout/LoginLayout';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { ResetPasswordFormDefaultValues } from 'constants/auth';
import { useMutation } from 'react-query';
import { useAtomValue } from 'jotai';
import { findPasswordUserInfoAtom, userInfoAtom } from 'store/auth';
import { axiosInstance, isTCustomAxiosError } from 'apis/config/axios';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from 'constants/routePath';
import {
  postPasswordChangeByLoginId,
  postPasswordChangeByToken,
} from 'apis/auth';
import useModal from 'hooks/useModal';
import {
  PasswordChangeFormFields,
  TPasswordChangeForm,
  TPasswordChangeParams,
} from 'types/form/auth';
import useAuth from 'hooks/useAuth';

const ResetPassword = () => {
  const passwordPattern =
    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,16}$/;
  const title = '비밀번호 재설정';
  const subtitle =
    '이전 비밀번호와 다른 비밀번호로 새 비밀번호를 입력해 주세요.\n 공백없이 영문, 숫자, 특수문자를 포함한 8~16자리를 입력해 주세요.';

  const userInfo = useAtomValue(userInfoAtom);
  const { isLogin, setIsLogin } = useAuth();
  const { openModal } = useModal();
  const navigate = useNavigate();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const findPasswordUserInfo = useAtomValue(findPasswordUserInfoAtom);

  const methods = useForm<TPasswordChangeForm>({
    defaultValues: ResetPasswordFormDefaultValues,
  });
  const {
    handleSubmit,
    control,
    formState: { errors: formErrors },
  } = methods;
  const firstFormErrorKey = Object.keys(
    formErrors
  )[0] as keyof TPasswordChangeForm;

  const [newPassword, newPasswordCheck] = useWatch({
    control,
    name: [
      PasswordChangeFormFields.NewPassword,
      PasswordChangeFormFields.NewPasswordCheck,
    ],
  });

  // 유저가 로그인한 상태일 때 (비밀번호를 변경한지 180일 이상 지났을 때 or 최초 로그인 시)
  const {
    mutate: passwordChangeByTokenMutation,
    isLoading: isChangingByToken,
  } = useMutation('passwordChangeByToken', postPasswordChangeByToken, {
    onSuccess: () => {
      userInfo?.isFirstSetPw
        ? openModal({
            title: '비밀번호 변경 완료',
            text: '비밀번호 변경이 완료되었습니다.',
            onSubmit: () => logoutAndNavigateToLogin(),
            onClickOutside: () => logoutAndNavigateToLogin(),
          })
        : openModal({
            title: '비밀번호 변경 완료',
            text: '비밀번호 변경이 완료되었습니다.',
            onSubmit: () => navigate(ROUTE_PATH.common.TERMS),
            onClickOutside: () => navigate(ROUTE_PATH.common.TERMS),
          });
    },
    onError: (error) => {
      if (isTCustomAxiosError(error)) {
        if (error.code === 409) {
          setErrorMsg('이전 비밀번호와 동일한 비밀번호입니다.');
        } else {
          setErrorMsg(error.message || '비밀번호 변경에 실패했습니다.');
        }
      } else {
        setErrorMsg(
          '비밀번호 변경에 실패했습니다. 잠시 후 다시 시도해 주세요.'
        );
      }
    },
  });

  // 유저가 로그인한 하지 않은 상태일 때 (비밀번호 찾기를 통해 접근했을 때)
  const {
    mutate: passwordChangeByLoginIdMutation,
    isLoading: isChangingByLoginId,
  } = useMutation('passwordChangeByLoginId', postPasswordChangeByLoginId, {
    onSuccess: () => {
      openModal({
        title: '비밀번호 변경 완료',
        text: '비밀번호 변경이 완료되었습니다.',
        onSubmit: () => logoutAndNavigateToLogin(),
        onClickOutside: () => logoutAndNavigateToLogin(),
      });
    },
    onError: (error) => {
      if (isTCustomAxiosError(error)) {
        if (error.code === 409) {
          setErrorMsg('이전 비밀번호와 동일한 비밀번호입니다.');
        } else {
          setErrorMsg(error.message || '비밀번호 변경에 실패했습니다.');
        }
      } else {
        setErrorMsg(
          '비밀번호 변경에 실패했습니다. 잠시 후 다시 시도해 주세요.'
        );
      }
    },
  });

  const onValid = (data: TPasswordChangeParams) => {
    setErrorMsg('');
    if (isLogin?.isLogin) {
      passwordChangeByTokenMutation({
        newPassword: data.newPassword,
        newPasswordCheck: data.newPasswordCheck,
      });
    } else {
      if (
        findPasswordUserInfo.userRole !== null &&
        findPasswordUserInfo.loginId !== null
      ) {
        passwordChangeByLoginIdMutation({
          newPassword: data.newPassword,
          newPasswordCheck: data.newPasswordCheck,
          loginId: findPasswordUserInfo.loginId,
          userRole: findPasswordUserInfo.userRole,
        });
      }
    }
  };

  const logoutAndNavigateToLogin = () => {
    sessionStorage.clear();
    axiosInstance.defaults.headers.Authorization = '';
    setIsLogin({ isLogin: false });
    navigate(ROUTE_PATH.common.LOGIN);
  };

  useEffect(() => {
    if (errorMsg && newPassword !== newPasswordCheck) {
      setErrorMsg('');
    }
  }, [newPassword, newPasswordCheck]);

  return (
    <LoginLayout title={title} subtitle={subtitle}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onValid)}>
          <div className={styles.input_wrapper}>
            <LabelInput
              labelText="새 비밀번호"
              inputType="password"
              placeholderText="새 비밀번호를 입력해 주세요."
              name={PasswordChangeFormFields.NewPassword}
              required={true}
              rules={{
                required: '비밀번호를 입력해 주세요.',
                minLength: {
                  value: 8,
                  message: '비밀번호는 최소 8자 이상이어야 합니다.',
                },
                maxLength: {
                  value: 16,
                  message: '비밀번호는 최대 16자까지 가능합니다.',
                },
                pattern: {
                  value: passwordPattern,
                  message:
                    '영문, 숫자, 특수문자(!@#$%^&*)를 각각 포함해 주세요.',
                },
              }}
            />

            <LabelInput
              labelText="비밀번호 확인"
              inputType="password"
              placeholderText="비밀번호를 재입력해 주세요."
              name={PasswordChangeFormFields.NewPasswordCheck}
              required={true}
              rules={{
                required: '비밀번호를 입력해 주세요.',
                validate: (value) => {
                  return (
                    value === newPassword ||
                    '비밀번호가 일치하지 않습니다. 다시 한번 확인해 주세요.'
                  );
                },
              }}
            />
          </div>

          {!errorMsg && !firstFormErrorKey && (
            <div className={styles.div}></div>
          )}
          {errorMsg && <span className={styles.error}>{errorMsg}</span>}
          {firstFormErrorKey && (
            <span className={styles.error}>
              {formErrors[firstFormErrorKey]?.message}
            </span>
          )}

          <SingleBtn
            type="submit"
            btnText="비밀번호 재설정"
            isBtnActive={true}
            btnStyle={{ width: '100%' }}
            disabled={isChangingByToken || isChangingByLoginId}
          />
        </form>
      </FormProvider>
    </LoginLayout>
  );
};

export default ResetPassword;
